import { useEffect, useRef, useState, DragEvent, ChangeEvent } from "react";
import useTranslator from "../../../../hooks/useTranslator";
import { CiMobile2 } from "react-icons/ci";
import { MdPhotoCamera } from "react-icons/md";
import { FaCheck } from "react-icons/fa";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PublicIcon from "@mui/icons-material/Public";
import styles from "../authenticateYourId.module.css";

interface FileUploadScreenProps {
  title: string;
  subtitle: string;
  onFileSelect: (file: File | Blob) => Promise<void>;
  acceptLabel?: string;
  onContinueOnPhone?: () => void;
  onTakePhoto?: () => void;
  docType?: string;
  side?: "front" | "back";
  showStepBadge?: boolean;
}

const DOCUMENT_TIPS = [
  "documentTipGoodLight",
  "documentTipFourCorners",
  "documentTipDataUncovered",
] as const;

const DOCUMENT_TIP_FALLBACKS: Record<(typeof DOCUMENT_TIPS)[number], string> = {
  documentTipGoodLight: "With good light and no reflections",
  documentTipFourCorners: "All four corners visible",
  documentTipDataUncovered: "Data and photo uncovered",
};

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const getHeroIcon = (docType?: string, side?: "front" | "back") => {
  if (docType === "passport") {
    return "/images/passport-icon.svg";
  }
  if (side === "back") {
    return "/images/docs-back-upload.svg";
  }
  if (docType === "residence_permit") {
    return "/images/resident.svg";
  }
  return "/images/idcard-icon.svg";
};

const FileUploadScreen = ({
  title,
  subtitle,
  onFileSelect,
  acceptLabel,
  onContinueOnPhone,
  onTakePhoto,
  docType,
  side = "front",
  showStepBadge = false,
}: FileUploadScreenProps) => {
  const { _t } = useTranslator();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isHeroFlipped, setIsHeroFlipped] = useState(false);

  const HeroIcon = getHeroIcon(docType, side);
  const frontHeroIcon = getHeroIcon(docType, "front");
  const backHeroIcon = getHeroIcon(docType, "back");
  const shouldFlipHero = side === "back" && docType !== "passport";

  const stepBadgeLabel =
    side === "back"
      ? getTranslatedText(_t, "documentBackStepBadge", "Back · 2 of 2")
      : getTranslatedText(_t, "documentFrontStepBadge", "Front · 1 of 2");

  useEffect(() => {
    if (!shouldFlipHero) return undefined;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setIsHeroFlipped(true);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsHeroFlipped(true);
    }, 450);

    return () => window.clearTimeout(timer);
  }, [shouldFlipHero]);

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(pendingFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [pendingFile]);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTriggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    clearFileInput();
  };

  const hasDraggedFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (event: DragEvent) => {
    if (!hasDraggedFiles(event) || isSubmitting) return;

    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleDragOver = (event: DragEvent) => {
    if (!hasDraggedFiles(event) || isSubmitting) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event: DragEvent) => {
    if (dragDepthRef.current === 0) return;

    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);

    if (isSubmitting) return;

    const file = event.dataTransfer.files?.[0];
    if (file) setPendingFile(file);
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onFileSelect(pendingFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`${styles.customKycUploadScreen} ${
        isDraggingFile ? "is-dragging-file" : ""
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingFile && (
        <div className={styles.customKycUploadDropOverlay} aria-hidden="true">
          <div className={styles.customKycUploadDropMessage}>
            {_t("dropTheFileHere")}
          </div>
        </div>
      )}

      {pendingFile ? (
        <div className={styles.customKycUploadReview}>
          <span className={styles.customKycUploadReviewEyebrow}>
            {getTranslatedText(_t, "photoReady", "Photo ready")}
          </span>
          <h3>
            {side === "back"
              ? getTranslatedText(
                  _t,
                  "doesBackLookGood",
                  "Does the back look good?"
                )
              : getTranslatedText(
                  _t,
                  "doesFrontLookGood",
                  "Does the front look good?"
                )}
          </h3>
          <div className={styles.customKycUploadPreviewFrame}>
            <img src={previewUrl} alt={_t("documentPreview")} />
          </div>
          <p className={styles.customKycUploadReviewHint}>
            {getTranslatedText(
              _t,
              "checkReadableNoReflections",
              "Check that everything is readable and there are no reflections"
            )}
          </p>
        </div>
      ) : (
        <div className={styles.customKycUploadIntro}>
          <div
            className={`${styles.customKycUploadHero} ${
              shouldFlipHero ? styles.hasFlip : ""
            }`}
          >
            {shouldFlipHero ? (
              <div className={styles.heroScene} aria-hidden="true">
                <div
                  className={`${styles.heroFlip} ${
                    isHeroFlipped ? styles.isFlipped : ""
                  }`}
                >
                  <div
                    className={`${styles.heroFace} ${styles.heroFaceFront}`}
                  >
                    <img src={frontHeroIcon} alt="" />
                  </div>
                  <div
                    className={`${styles.heroFace} ${styles.heroFaceBack}`}
                  >
                    <img src={backHeroIcon} alt="" />
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={HeroIcon}
                className={`${styles.customKycUploadHeroIcon} ${
                  HeroIcon === "/images/passport-icon.svg"
                    ? styles.customKycUploadHeroIconPassport
                    : ""
                }`}
                alt=""
              />
            )}
          </div>
          <div className={styles.headingWithBadge}>
            <h3>{title}</h3>
            {showStepBadge && (
              <span className={styles.headingBadge}>{stepBadgeLabel}</span>
            )}
          </div>
          {subtitle && <p>{subtitle}</p>}
          <div className={styles.docInfoList}>
            {DOCUMENT_TIPS.map((tipKey) => (
              <div key={tipKey}>
                <FaCheck color="#253837" size={12} aria-hidden="true" />
                <span>
                  {getTranslatedText(
                    _t,
                    tipKey,
                    DOCUMENT_TIP_FALLBACKS[tipKey]
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.customKycUploadActionPanel}>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptLabel || ".jpg,.jpeg,.png"}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {pendingFile ? (
          <div className={styles.customKycUploadReviewActions}>
            <button
              type="button"
              className={`${styles.customKycUploadSecondaryBtn} ${styles.continueOnPhoneBtn}`}
              disabled={isSubmitting}
              onClick={() => {
                setPendingFile(null);
                clearFileInput();
              }}
            >
              {getTranslatedText(_t, "retake", "Retake")}
            </button>
            <button
              type="button"
              className={styles.customKycUploadConfirmBtn}
              disabled={isSubmitting}
              onClick={handleUploadConfirm}
            >
              {isSubmitting
                ? _t("uploading")
                : getTranslatedText(_t, "usePhoto", "Use photo")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.customKycUploadPrimaryBtn}
            onClick={handleTriggerFileInput}
          >
            <img src="/images/upload-icon.svg" alt="" />
            {_t("uploadPhoto")}
          </button>
        )}
        {onContinueOnPhone && !pendingFile && (
          <button
            type="button"
            className={`${styles.customKycUploadSecondaryBtn} ${styles.continueOnPhoneBtn}`}
            onClick={onContinueOnPhone}
          >
            <CiMobile2 color="#253837" size={18} />
            {_t("continueOnPhone")}
          </button>
        )}
        {onTakePhoto && !pendingFile && (
          <button
            type="button"
            className={`${styles.customKycUploadSecondaryBtn} ${styles.continueOnPhoneBtn}`}
            onClick={onTakePhoto}
          >
            <MdPhotoCamera color="#253837" size={18} />
            {_t("takePhoto")}
          </button>
        )}
      </div>
    </div>
  );
};

export default FileUploadScreen;
